import React, {useContext, useEffect, useState} from 'react'
import {Basic} from 'types'

declare global {
  interface Window {
    directCheckoutWidgetConfig: Basic.Config
  }
}

type Props = Basic.Input & {
  children: React.ReactNode
}

class NotPassedError extends Error {
  constructor(parameter: string) {
    super(`Parameter not passed: ${parameter}`)
  }
}

export const EnvironmentContext = React.createContext<Basic.Environment | null>(null)

export const EnvironmentProvider = ({children, ...input}: Props) => {
  const siteUrl = window.location.origin
  const [config, setConfig] = useState(window.directCheckoutWidgetConfig)

  useEffect(() => {
    const listener = () => {
      setConfig({...window.directCheckoutWidgetConfig})
    }

    document.addEventListener('directCheckoutWidgetConfigChanged', listener)

    return () => document.removeEventListener('directCheckoutWidgetConfigChanged', listener)
  }, [])

  if (config.currency && !['EUR', 'USD', 'AUD', 'CAD', 'GBP'].includes(config.currency)) {
    throw new Error(`Unknown currency '${config.currency}'`)
  }

  const value = {
    ...config,
    ...input,
    siteUrl,
  }

  return <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>
}

export const useEnvironment = () => {
  const environment = useContext(EnvironmentContext)

  if (!environment) {
    throw new Error('Environment is null')
  }

  return environment
}

export const useCurrency = () => {
  const {currency} = useEnvironment()

  if (!currency) {
    throw new NotPassedError(`currency`)
  }

  return currency
}

export const useProductId = () => {
  const {productId} = useEnvironment()

  if (!productId) {
    throw new NotPassedError(`productId`)
  }

  return productId
}

export const useOptionalOptionId = () => {
  const {optionId} = useEnvironment()
  return optionId
}

export const useApiKey = () => {
  const {apiKey} = useEnvironment()

  if (!apiKey) {
    throw new NotPassedError(`apiKey`)
  }

  return apiKey
}

export const useSiteUrl = () => {
  const {siteUrl} = useEnvironment()

  if (!siteUrl) {
    throw new NotPassedError(`siteUrl`)
  }

  return siteUrl
}

export const useShowMoreToursPage = () => {
  const {showMoreToursPage} = useEnvironment()
  return !!showMoreToursPage
}

export const useShowSecurePaymentMessage = () => {
  const {showSecurePaymentMessage} = useEnvironment()
  return !!showSecurePaymentMessage
}

export const useShowSocialProofBadges = () => {
  const {showSocialProofBadges} = useEnvironment()
  return !!showSocialProofBadges
}

export const useHideAddToCartAndKeepBrowsingButton = () => {
  const {hideAddToCartAndKeepBrowsingButton} = useEnvironment()
  return !!hideAddToCartAndKeepBrowsingButton
}

const usePath = (path: keyof Required<Basic.Environment>['paths']): string => {
  const {paths} = useEnvironment()

  if (!paths || !paths[path]) {
    throw new NotPassedError(`paths.${path}`)
  }

  return paths[path] as string
}

export const useCartPath = () => {
  return usePath('cart')
}

export const useTravelerPath = () => {
  return usePath('traveler')
}

export const useActivityPath = () => {
  return usePath('activity')
}

export const useMoreToursPath = () => {
  return usePath('moreTours')
}

export const usePaymentPath = () => {
  return usePath('payment')
}

export const useConfirmingPath = () => {
  return usePath('confirming')
}

export const useOrderPath = () => {
  return usePath('order')
}

export const useExploreMorePath = () => {
  return usePath('exploreMore')
}

export const useContactUsPath = () => {
  return usePath('contactUs')
}

export const useTermsPath = () => {
  return usePath('terms')
}

export const useAppearance = () => {
  const {appearance} = useEnvironment()
  return appearance
}

EnvironmentProvider.useOptionalOptionId = useOptionalOptionId
EnvironmentProvider.useProductId = useProductId
EnvironmentProvider.useCurrency = useCurrency

EnvironmentProvider.useVariations = () => {
  const {variations} = useEnvironment()
  return variations || null
}
